export enum GenerationStatus {
  IDLE = 'IDLE',
  PLANNING = 'PLANNING',
  GENERATING = 'GENERATING',
  SUMMARIZING = 'SUMMARIZING',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR'
}

// ============ PROJECT TYPE ============

export type ProjectType = 'guide' | 'novel' | 'essay';

// Citation styles for essays
export type CitationStyle = 'none' | 'apa' | 'mla' | 'chicago' | 'ieee' | 'vancouver' | 'harvard' | 'iso690';

export const CITATION_STYLES: Record<CitationStyle, { name: string; desc: string; system: 'author-date' | 'footnote' | 'numeric' }> = {
  none: { name: 'Sin citas', desc: 'Sin referencias ni bibliografía', system: 'author-date' },
  apa: { name: 'APA', desc: 'Autor-fecha (Apellido, año). Ciencias sociales y psicología.', system: 'author-date' },
  mla: { name: 'MLA', desc: 'Autor-página (Apellido página). Humanidades y literatura.', system: 'author-date' },
  chicago: { name: 'Chicago', desc: 'Notas al pie con superíndice. Historia y arte.', system: 'footnote' },
  ieee: { name: 'IEEE', desc: 'Numérico [1]. Ingeniería y tecnología.', system: 'numeric' },
  vancouver: { name: 'Vancouver', desc: 'Numérico consecutivo. Medicina y salud.', system: 'numeric' },
  harvard: { name: 'Harvard', desc: 'Autor-fecha (Apellido año). Multidisciplinar.', system: 'author-date' },
  iso690: { name: 'ISO 690', desc: 'Notas al pie + bibliografía. Norma internacional.', system: 'footnote' },
};

// Tonos para guías/manuales
export const GUIDE_TONES = ['Inspirador', 'Académico', 'Cercano', 'Directo'] as const;
export type GuideTone = typeof GUIDE_TONES[number];

// Tonos para ensayos (incluye humorístico)
export const ESSAY_TONES = [
  'Académico',    // Formal, riguroso  
  'Cercano',      // Conversacional, accesible
  'Humorístico',  // Satírico, ingenioso
  'Reflexivo',    // Introspectivo, filosófico
  'Provocador',   // Polémico, desafiante
  'Narrativo'     // Cuenta historias para ilustrar ideas
] as const;
export type EssayTone = typeof ESSAY_TONES[number];

// Tonos narrativos para novelas
export const NOVEL_TONES = [
  'Épico',       // Fantasía épica, aventuras
  'Íntimo',      // Drama personal, romance  
  'Noir',        // Thriller, misterio
  'Onírico',     // Realismo mágico, ficción literaria
  'Trepidante',  // Thriller de acción
  'Irónico',     // Sátira, comedia negra
  'Gótico',      // Horror, gótico
  'Minimalista'  // Ficción contemporánea
] as const;
export type NovelTone = typeof NOVEL_TONES[number];

// Roles narrativos basados en Vogler (El Camino del Escritor), Campbell (El Héroe de las Mil Caras), McKee (El Guión)
export const CHARACTER_ROLES = [
  'Hero',               // Héroe / Protagonista — el centro de la historia
  'Mentor',             // Mentor / Guía — quien prepara al héroe
  'Shadow',             // Sombra / Antagonista — fuerza opositora principal
  'Threshold Guardian', // Guardián del Umbral — pone a prueba al héroe
  'Herald',             // Heraldo / Mensajero — anuncia el cambio
  'Shapeshifter',       // Cambiante — lealtad ambigua
  'Trickster',          // Embaucador / Bufón — alivio cómico, caos
  'Ally',               // Aliado / Compañero — apoyo del protagonista
  'Love Interest',      // Interés Romántico — tensión emocional
  'Contagonist',        // Contagonista — obstaculiza sin ser villano
  'Narrator',           // Narrador — voz que cuenta la historia
  'Symbolic'            // Simbólico / Arquetipo libre
] as const;
export type CharacterRole = typeof CHARACTER_ROLES[number];

export interface NovelCharacter {
  id: string;
  name: string;
  role: CharacterRole;
  description: string;  // máx ~300 chars
}

// Estructura narrativa basada en Save the Cat + Viaje del Héroe
export interface NarrativeStructure {
  // ACTO 1: SETUP (0-25%)
  openingImage: string;        // Mundo ordinario inicial
  themeStated: string;         // Premisa temática sutil
  catalyst: string;            // Llamada a la aventura
  debate: string;              // Rechazo de la llamada

  // ACTO 2A: CONFRONTATION (25-50%)
  breakIntoTwo: string;        // Cruce del umbral
  bStory: string;              // Subplot / Relación clave
  funAndGames: string;         // Pruebas y aliados
  midpoint: string;            // Revelación central / Falsa victoria

  // ACTO 2B: COMPLICATION (50-75%)
  badGuysCloseIn: string;      // Las fuerzas se reagrupan
  allIsLost: string;           // Muerte simbólica / Cueva más profunda
  darkNightOfTheSoul: string;  // Momento de desesperación absoluta

  // ACTO 3: RESOLUTION (75-100%)
  breakIntoThree: string;      // Epifanía / Elixir encontrado
  finale: string;              // Batalla final / Clímax
  finalImage: string;          // Nuevo mundo ordinario
}



// ============ GUIDE STRUCTURE (unchanged) ============

export interface Section {
  id: string;
  title: string;
  description: string;
  content?: string;
  summary?: string; // Used for context in subsequent sections
  status: 'pending' | 'generating' | 'done' | 'error';

  beat?: string; // For novels: which narrative beat this section represents
}

export interface Chapter {
  id: string;
  title: string;
  sections: Section[];
}

// ============ NOVEL STRUCTURE (NEW - 4 levels) ============

// Segment: A generative block of ~1000 words within a scene
export interface Segment {
  id: string;
  order: number;              // Position within the scene (1, 2, 3...)
  content?: string;
  summary?: string;
  status: 'pending' | 'generating' | 'done' | 'error';
}

// Scene: A complete dramatic unit that may require multiple segments
export type SceneComplexity = 'simple' | 'medium' | 'complex';

export interface Scene {
  id: string;
  title: string;              // Evocative scene title
  description: string;        // Detailed instruction for the writer
  beat?: string;              // Narrative beat (opening_image, catalyst, etc.)
  complexity: SceneComplexity;
  estimatedSegments: number;  // How many ~1000 word segments (1-4)
  segments: Segment[];        // The generative blocks
}

// NovelChapter: A chapter containing multiple scenes, belonging to an act
export interface NovelChapter {
  id: string;
  title: string;
  actNumber: number;
  actName: string;            // "Preparación", "Confrontación", "Resolución"
  actDescription?: string;    // Dramatic purpose of the act
  scenes: Scene[];
}

// Narrative beats for reference
export const NARRATIVE_BEATS = [
  'opening_image', 'theme_stated', 'setup', 'catalyst', 'debate',
  'break_into_two', 'b_story', 'fun_and_games', 'midpoint',
  'bad_guys_close_in', 'all_is_lost', 'dark_night',
  'break_into_three', 'finale', 'final_image'
] as const;
export type NarrativeBeat = typeof NARRATIVE_BEATS[number];

export type EntityType = 'character' | 'location' | 'item' | 'event' | 'relationship';

export interface BibleEntity {
  id: string;
  type: EntityType;
  name: string;
  description: string;
  attributes: Record<string, string>; // e.g. { "Age": "30", "Eyes": "Blue" }
  aliases: string[]; // For detection (e.g. "Bob", "Robert", "Mr. Smith")
}

export interface StoryBible {
  entities: BibleEntity[];
  lastUpdated: number;
}

// How the project was initiated
export type ProjectOrigin = 'ai_generated' | 'blank' | 'draft_import';

export interface BookProject {
  id: string; // Unique identifier for persistence
  title: string;
  topic: string;
  targetAudience: string;
  tone: string;
  language: string;
  targetWordCount?: number;
  outline: Chapter[];              // Used for guides (unchanged)
  novelOutline?: NovelChapter[];   // NEW: Used for novels (4-level structure)
  bible?: StoryBible; // Structured Memory
  createdAt: number;
  lastUpdated: number;
  isCloudOnly?: boolean; // If true, content is not available locally (ghost project)
  // Guide mode
  guideDescription?: string;               // Optional: detailed description of guide focus
  // Novel mode
  projectType: ProjectType; // 'guide' | 'novel' | 'essay'
  narrativeStructure?: NarrativeStructure; // Only for novels
  novelCharacters?: NovelCharacter[];      // Optional: up to 12 pre-defined characters
  // Essay mode
  essaySynopsis?: string;          // Shorter synopsis for essays (max ~1000 chars)
  essayReferences?: boolean;       // Legacy — kept for backward compat
  essayCitationStyle?: CitationStyle;  // Citation style for essays
  // Draft import
  isDraftImport?: boolean;         // Flag: fue importado como borrador
  draftOriginalWordCount?: number; // Palabras del borrador original
  // === UNIFIED WORKSPACE FIELDS (from Assistant module) ===
  projectOrigin?: ProjectOrigin;           // How was the project created
  brainstormHistory?: BrainstormMessage[]; // Chat brainstorm history
  savedTwists?: PlotTwist[];               // Saved twist/case ideas
}

export type BookContext = {
  project: BookProject | null;
  setProject: (project: BookProject | null) => void;
  updateSection: (chapterIndex: number, sectionIndex: number, updates: Partial<Section>) => void;
  updateBible: (newBible: StoryBible) => void;
};

// ============ AUTH & USER TYPES ============

export type PlanTier = 'free' | 'starter' | 'professional' | 'agency' | 'single_book';

export interface User {
  id: string;
  email: string;
  name: string;
  plan: PlanTier;
  booksRemaining: number; // Renamed from credits
  stripeCustomerId?: string;
  createdAt: number;
  lastLogin: number;
  // Usage Tracking
  regenerationsUsed: number;
  auditsUsed: number;
  usagePeriodStart: number;
  freeGenerationUsed?: boolean;
  singleBookProjectId?: string; // For single_book plan - which project it applies to
  // Assistant Module
  assistantPlan?: AssistantPlanTier;
  assistantUsage?: AssistantUsage;
}

// Guest session for unauthenticated users
export interface GuestSession {
  guestId: string;
  projectId: string | null;
  firstSegmentGenerated: boolean;
  createdAt: number;
}

export interface Session {
  userId: string;
  email: string;
  token: string;
  expiresAt: number;
}

export interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

// Stripe Price IDs (Live Mode) — EUR (Europe)
export const STRIPE_PRICES = {
  starter: 'price_1Sv3gIJibuRQpdrB2tHZvwEQ',
  professional: 'price_1Sv3iAJibuRQpdrBnTLEsl81',
  agency: 'price_1Sv3jNJibuRQpdrBuucSLXZC'
} as const;

// Stripe Price IDs (Live Mode) — USD (Americas, Asia, rest of world)
export const STRIPE_PRICES_USD = {
  starter: 'price_1TGkyJJibuRQpdrBTWqLBlWC',
  professional: 'price_1TGkzKJibuRQpdrB06UiyojL',
  agency: 'price_1TGkzwJibuRQpdrB8BFGpy5V'
} as const;

export const PLAN_DETAILS: Record<PlanTier, { name: string; booksLimit: number; price: number }> = {
  free: { name: 'Prueba Gratuita', booksLimit: 0, price: 0 }, // 0 here because Free logic is special (1 total)
  starter: { name: 'Escritor Novel', booksLimit: 1, price: 19 },
  professional: { name: 'Autor Pro', booksLimit: 3, price: 39 },
  agency: { name: 'Agencia VIP', booksLimit: 8, price: 89 },
  single_book: { name: 'Libro Individual', booksLimit: 1, price: 29 }
};

export const PLAN_LIMITS = {
  free: { regenerations: 1, audits: 1 },
  starter: { regenerations: 20, audits: 20 },
  professional: { regenerations: 80, audits: 80 },
  agency: { regenerations: 250, audits: 250 },
  single_book: { regenerations: 20, audits: 20 } // Same as starter
} as const;

// ============ ASSISTANT MODULE TYPES ============

export type AssistantPlanTier = 'assistant_free' | 'assistant_plus' | 'assistant_pro';

// NOTE: Assistant module no longer has separate Stripe prices.
// All payments go through the unified Writer plans (STRIPE_PRICES).

export const ASSISTANT_PLAN_DETAILS: Record<AssistantPlanTier, {
  name: string;
  projects: number;
  price: number;
}> = {
  assistant_free: { name: 'Autor Free', projects: 1, price: 0 },
  assistant_plus: { name: 'Autor Plus', projects: 3, price: 9 },
  assistant_pro:  { name: 'Autor Pro',  projects: 10, price: 19 },
};

export const ASSISTANT_PLAN_LIMITS: Record<AssistantPlanTier, {
  outlineRegens: number;
  twists: number;
  openings: number;
  brainstormMsgs: number;    // Per month
  pacingAnalysis: number;
  consistencyChecks: number;
  betaReads: number;
  styleRewrites: number;     // NEW: Style rewriting
  continuations: number;     // NEW: Smart continuation
}> = {
  assistant_free: {
    outlineRegens: 0, twists: 0, openings: 3,
    brainstormMsgs: 4, // 2/día máx, 4 mensajes mensuales totales
    pacingAnalysis: 0, consistencyChecks: 0, betaReads: 0,
    styleRewrites: 3, continuations: 0
  },
  assistant_plus: {
    outlineRegens: 5, twists: 10, openings: 20,
    brainstormMsgs: 50, pacingAnalysis: 3, consistencyChecks: 3, betaReads: 0,
    styleRewrites: 20, continuations: 10
  },
  assistant_pro: {
    outlineRegens: 20, twists: 50, openings: 100,
    brainstormMsgs: 150, pacingAnalysis: 15, consistencyChecks: 15, betaReads: 5,
    styleRewrites: 100, continuations: 30
  }
} as const;

// Free plan daily brainstorm limit (enforced client + server side)
export const ASSISTANT_FREE_DAILY_BRAINSTORM = 2;

// ============ ASSISTANT PROJECT TYPES ============

export interface AssistantProject {
  id: string;
  title: string;
  idea: string;                    // Synopsis/idea from the author
  projectType: ProjectType;        // 'guide' | 'novel' | 'essay'
  targetAudience: string;
  tone: string;
  language: string;

  // AI-generated outline
  outline?: AssistantOutline;

  // Author's manuscript content (pasted/imported)
  chapters?: AssistantChapter[];

  // Project bible (reuse existing StoryBible)
  bible?: StoryBible;

  // Characters (for novels)
  characters?: NovelCharacter[];

  // Brainstorm chat history
  brainstormHistory?: BrainstormMessage[];

  // Saved plot twist ideas
  savedTwists?: PlotTwist[];

  // Metadata
  createdAt: number;
  lastUpdated: number;
  wordCount: number;
}

// ============ ASSISTANT OUTLINE ============

export interface AssistantOutline {
  type: 'basic' | 'advanced';
  chapters: AssistantOutlineChapter[];
  generatedAt: number;
}

export interface AssistantOutlineChapter {
  id: string;
  title: string;
  description: string;
  beat?: string;                   // For novels: narrative beat
  actNumber?: number;              // For novels: act (1, 2, 3)
  actName?: string;                // For novels: act name
  sections?: AssistantOutlineSection[];
}

export interface AssistantOutlineSection {
  id: string;
  title: string;
  description: string;
}

// ============ ASSISTANT MANUSCRIPT ============

export interface AssistantChapter {
  id: string;
  title: string;
  content: string;                 // Markdown content (pasted by author)
  summary?: string;                // Auto-generated summary for AI context
  wordCount: number;
  lastUpdated: number;
}

// ============ BRAINSTORM CHAT ============

export interface BrainstormMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

// ============ PLOT TWIST GENERATOR ============

export interface PlotTwist {
  id: string;
  title: string;
  description: string;
  impact: string;                  // Impact on characters/plot
  insertionPoint: string;          // Where to insert it in the story
  intensity: 'subtle' | 'medium' | 'dramatic';
}

// ============ PACING ANALYSIS ============

export interface PacingResult {
  overallScore: number;            // 0-100
  chapterAnalysis: ChapterPacing[];
  suggestions: PacingSuggestion[];
  analyzedAt: number;
}

export interface ChapterPacing {
  chapterId: string;
  chapterTitle: string;
  tensionLevel: number;           // 0-100
  wordCount: number;
  relativeLength: 'too_short' | 'appropriate' | 'too_long';
  dominantMood: string;           // e.g. "tense", "calm", "action", "reflective"
}

export interface PacingSuggestion {
  type: 'warning' | 'tip';
  chapter: string;
  message: string;
}

// ============ CONSISTENCY CHECKER ============

export interface ConsistencyResult {
  issues: ConsistencyIssue[];
  checkedAt: number;
  chaptersAnalyzed: number;
}

export interface ConsistencyIssue {
  id: string;
  severity: 'error' | 'warning' | 'info';
  category: 'character' | 'timeline' | 'location' | 'object' | 'logic' | 'argument' | 'reference';
  title: string;
  description: string;
  chapter: string;                 // Which chapter has the issue
  relatedChapter?: string;         // Related chapter (for cross-chapter inconsistencies)
}

// ============ BETA READER ============

export interface BetaReaderReport {
  overallEngagement: number;       // 0-100
  chapterReviews: ChapterReview[];
  strengths: string[];
  weaknesses: string[];
  topRecommendations: string[];
  readerPredictions: ReaderPrediction[];
  reportedAt: number;
}

export interface ChapterReview {
  chapterId: string;
  chapterTitle: string;
  engagementScore: number;        // 0-100
  confusionPoints: string[];
  highlights: string[];           // Best parts
  putDownRisk: 'low' | 'medium' | 'high';  // Would reader stop reading here?
}

export interface ReaderPrediction {
  afterChapter: string;
  prediction: string;             // What the reader would expect next
  surpriseLevel: 'expected' | 'interesting' | 'surprising' | 'shocking';
}

// ============ ASSISTANT USAGE TRACKING ============

export interface AssistantUsage {
  outlineRegensUsed: number;
  twistsUsed: number;
  openingsUsed: number;
  brainstormMsgsUsed: number;
  pacingAnalysisUsed: number;
  consistencyChecksUsed: number;
  betaReadsUsed: number;
  styleRewritesUsed: number;      // NEW: Style rewriting counter
  continuationsUsed: number;      // NEW: Smart continuation counter
  usagePeriodStart: number;
  // Daily tracking for Free plan brainstorm
  brainstormDailyCount: number;
  brainstormDailyDate: string;    // ISO date "YYYY-MM-DD" to reset daily
}